import { useEffect, useState } from 'react';

import { logger } from 'classes/logger';

export const bucketSeconds = 0.005;

/**
 * Loudness per 5 ms of the recording, 0..1 (fine enough that a fragment of under a second still gets thin bars).
 * A recording that grows gets the same buckets for the audio it already had
 * (the player merges them to bars that fit), so the waveform only gains bars on the right.
 */
export const useWaveform = (url: string, blob?: Blob) => {

  const [peaks, setPeaks] = useState<number[]>([]);

  useEffect(() => {

    const controller = new AbortController();

    const decode = async () => {

      // Offline: decoding needs no sound output, so no AudioContext that the browser holds back until a user gesture.
      const context = new OfflineAudioContext(1, 1, 44100);

      try {

        // A recording that is already in memory is not fetched again through its blob: url.
        const data = blob ? await blob.arrayBuffer() : await (await fetch(url, { signal: controller.signal })).arrayBuffer();
        const audio = await context.decodeAudioData(data);
        const samples = audio.getChannelData(0);
        const size = Math.round(audio.sampleRate * bucketSeconds);
        const loudness = Array.from({ length: Math.ceil(samples.length / size) }, (_, bucket) => samples
          .subarray(bucket * size, (bucket + 1) * size)
          .reduce((max, sample) => Math.max(max, Math.abs(sample)), 0));
        // Reduced, not spread: a long recording has more buckets than a function call takes arguments.
        const loudest = loudness.reduce((max, peak) => Math.max(max, peak), 0.05);

        if (!controller.signal.aborted) setPeaks(loudness.map((peak) => peak / loudest));

      } catch (error) {

        // The waveform is decoration: without it the bar still works, so only a warning (not Sentry). An abort is just a new url.
        if (!controller.signal.aborted) logger.warn('Recording waveform failed', error);

      }

    };

    decode();

    return () => controller.abort();

  }, [url, blob]);

  return peaks;

};
